<script setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useDMSSettingsStore } from '~/dms/store/dms-settings.store';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  customFieldMapping: {
    type: Object,
  },
  scenarios: {
    type: Array,
    default: () => [{ label: 'Entry', value: 'entry', fields: [{ label: 'Timestamp', uid: 'timestamp', type: 'date' }], options: [{ label: 'first time', value: 'first_time' }, { label: 'every time', value: 'every_time' }, { label: 'each revision', value: 'each_revision' }] }, { label: 'Exit', value: 'exit', options: [{ label: 'first time', value: 'first_time' }, { label: 'every time', value: 'every_time' }, { label: 'each revision', value: 'each_revision' }], fields: [{ label: 'Timestamp', uid: 'timestamp', type: 'date' }, { label: 'Outcome', uid: 'outcome', type: 'text' }] }],
  },
  appendTo: {
    type: String,
    default: '#transmittal-field-mapping',
  },
});

const emits = defineEmits(['mounted']);

const $t = inject('$t');
const el$ = ref(null);
const dms_settings_store = useDMSSettingsStore('custom_fields_mapping');
const { custom_fields, document_status } = storeToRefs(dms_settings_store);
const document_custom_fields = computed(() => custom_fields.value.filter(field => !field.deleted && field.allowed_in.includes('documents')));
const transmittal_custom_fields = computed(() => custom_fields.value.filter(field => !field.deleted && field.allowed_in.includes('transmittals')));
const active_document_statuses = computed(() => document_status.value.filter(status => status.active));

const has_mounted = ref(false);

async function getData() {
  await dms_settings_store.set_custom_fields();
  await dms_settings_store.fetch_all_statuses();
  emits('mounted');
  setTimeout(() => {
    has_mounted.value = true;
  }, 300);
}

getData();

function scenarioChanged(index) {
  if (!has_mounted.value)
    return;
  el$.value.form$.el$(`custom_field_mapping.config.${index}.source`)?.clear();
  el$.value.form$.el$(`custom_field_mapping.config.${index}.destination`)?.clear();
  el$.value.form$.el$(`custom_field_mapping.config.${index}.occurrence`)?.clear();
}

function sourceChanged(index) {
  if (!has_mounted.value)
    return;
  el$.value.form$.el$(`custom_field_mapping.config.${index}.destination`)?.clear();
}
function revisionSourceChanged(index, revision_index) {
  if (!has_mounted.value)
    return;
  el$.value.form$.el$(`custom_field_mapping.config.${index}.revision_config.${revision_index}.destination`)?.clear();
}

function getSourceFields(config) {
  const scenario = props.scenarios.find(scenario => scenario.value === config?.scenario);
  const source_fields = scenario?.fields || [];

  // Build the source fields array with transmittal fields and custom fields
  const transmittal_fields = source_fields.map(f => ({ ...f, field_type: 'transmittal_field' }));
  const custom_fields_list = transmittal_custom_fields.value.map(f => ({ ...f, field_type: 'custom_field' }));

  // Add document statuses with proper grouping
  const document_status_fields = active_document_statuses.value.map(status => ({
    label: status.name,
    uid: status.uid,
    type: 'document_status',
    field_type: 'document_status',
  }));

  // Combine all fields with grouping
  let all_fields = [];
  const fields = [...transmittal_fields.map(field => ({
    ...field,
    uid: field.uid,
    type: field.type,
    field_type: field.field_type,
    label: field.label,
  })), ...custom_fields_list.map(field => ({
    ...field,
    uid: field.uid,
    type: field.type,
    field_type: field.field_type,
    label: field.label,
  }))];
  // Add document statuses as a separate group if they exist
  // For non-workflow scenarios (submission, review_completion, approval_completion, request_for_resubmission),
  // the scenario itself is already filtered based on conditions (has_submitters, has_reviewers, has_approvers)
  // in create-transmittal-recipient.vue, so if the scenario is visible, document statuses should be shown
  if (document_status_fields.length > 0) {
    all_fields = [
      {
        label: $t('Fields'),
        items: fields,
      },
      {
        label: $t('Document Statuses'),
        items: document_status_fields.map(field => ({
          ...field,
          uid: field.uid,
          type: field.type,
          field_type: field.field_type,
          label: field.label,
        })),
      },
    ];
  }
  else {
    all_fields = fields;
  }

  return all_fields;
}

function getDestinationFields(config, revision_config) {
  if (config?.scenario) {
    const source_fields = getSourceFields(config);

    // Flatten the grouped structure to get all fields
    let all_source_fields = [];
    if (Array.isArray(source_fields)) {
      // Check if it's a grouped structure (array of objects with 'items' property)
      if (source_fields.length > 0 && source_fields[0]?.items) {
        // Flatten grouped structure
        source_fields.forEach((group) => {
          if (group.items) {
            all_source_fields = all_source_fields.concat(group.items);
          }
        });
      }
      else {
        // It's already a flat array
        all_source_fields = source_fields;
      }
    }

    let selected_source_field;
    if (revision_config?.source?.uid)
      selected_source_field = all_source_fields.find(field => field.uid === revision_config?.source?.uid);
    else
      selected_source_field = all_source_fields.find(field => field.uid === config?.source?.uid);

    // If source is document_status, return 'document_status' as the only destination option
    if (selected_source_field?.type === 'document_status') {
      return [{ label: 'Document status', uid: 'document_status', type: 'document_status' }];
    }

    return document_custom_fields.value.filter(field => field.type === (selected_source_field?.type || 'text'));
  }
  return document_custom_fields.value;
}

function getOccurrences(config) {
  const scenario = props.scenarios.find(scenario => scenario.value === config?.scenario);
  return scenario?.options || [];
}
</script>

<template>
  <ObjectElement name="custom_field_mapping" :disabled="disabled" @mounted="e => el$ = e">
    <div class="flex gap-4 my-4 col-span-12">
      <div class="flex-1">
        <div class="text-sm font-semibold">
          {{ $t("Field Mapping") }}
        </div>
        <div class="text-xs w-120">
          {{
            $t(
              "Sync workflow dates or review outcomes to file meta data",
            )
          }}.
        </div>
      </div>
      <ToggleElement
        :disabled="disabled"
        name="is_enabled"
      />
    </div>
    <div v-if="customFieldMapping?.is_enabled" class="col-span-12">
      <ListElement
        name="config"
        :disabled="disabled"
        :add-text="`+ ${$t('Add mapping')}`"
        :presets="['repeatable_list']"
        rules="required"
        min="1"
        :override-classes="{
          ListElement: {
            listItem: 'flex w-full bg-gray-50 p-4',
            remove: ['h-5 w-5 ml-auto mt-2'],
          },
        }"
      >
        <template
          #default="{ index }"
        >
          <ObjectElement
            :key="scenarios"
            :embed="true" :name="index" :add-classes="{
              ElementLayout: {
                container: 'w-full',
              },
            }"
          >
            <div class="grid  grid-cols-12 items-center gap-2 my-2">
              <div class="col-span-3 flex items-center">
                <span class="mx-1 text-gray-600">{{ $t('On') }}</span>

                <SelectElement
                  class="flex-1"
                  name="scenario"
                  :native="false"
                  rules="required"
                  :items="scenarios"
                  :can-clear="false"
                  :append-to="appendTo"
                  :add-classes="{
                    ElementLayout: {
                      outerWrapper: '!flex',
                      innerWrapperAfter: '!hidden',
                    },
                    SelectElement: {
                      select: {
                        container: '!border-b !border-t-0 !p-0 !min-h-3 h-6 focused:!rounded-none focused:!outline-none  !border-r-0 !border-l-0 !shadow-none !bg-transparent !rounded-none',
                      },
                    },
                  }"
                  @change="scenarioChanged(index)"
                />
              </div>
              <div v-if="customFieldMapping?.config?.[index]?.occurrence !== 'each_revision'" class="col-span-3 flex items-center">
                <span class="mx-1 text-gray-600">{{ $t('set') }}</span>

                <SelectElement
                  class="flex-1"
                  name="source"
                  :native="false"
                  :items="getSourceFields(customFieldMapping?.config?.[index])"
                  rules="required"
                  :create="true" :append-new-option="false"
                  :can-clear="false"
                  label-prop="label"
                  value-prop="uid"
                  :object="true"
                  :groups="true"
                  group-label="label"
                  :append-to="appendTo"
                  :add-classes="{
                    ElementLayout: {
                      outerWrapper: '!flex',
                      innerWrapperAfter: '!hidden',
                    },
                    SelectElement: {
                      select: {
                        container: '!border-b !border-t-0 !p-0 !min-h-3 h-6 focused:!rounded-none focused:!outline-none  !border-r-0 !border-l-0 !shadow-none !bg-transparent !rounded-none',
                      },
                    },
                  }"
                  @change="sourceChanged(index)"
                />
              </div>
              <div v-if="customFieldMapping?.config?.[index]?.occurrence !== 'each_revision'" class="col-span-3 flex items-center">
                <span class="mx-1 text-gray-600">{{ $t('to') }}</span>
                <SelectElement
                  class="flex-1"
                  name="destination"
                  :items="getDestinationFields(customFieldMapping?.config?.[index])"
                  label-prop="label"
                  value-prop="uid"
                  :append-to="appendTo"
                  :native="false"
                  :can-clear="false"
                  rules="required"
                  :add-classes="{
                    ElementLayout: {
                      outerWrapper: '!flex',
                      innerWrapperAfter: '!hidden',

                    },
                    SelectElement: {
                      select: {
                        container: '!border-b !border-t-0 !p-0 !min-h-3 h-6 focused:!rounded-none focused:!outline-none  !border-r-0 !border-l-0 !shadow-none !bg-transparent !rounded-none',
                      },
                    },
                  }"
                />
              </div>
              <div v-if="getOccurrences(customFieldMapping?.config?.[index]).length > 0" class="col-span-3 flex items-center" :class="{ '!col-span-5': customFieldMapping?.config?.[index]?.occurrence === 'each_revision' }">
                <span v-if="customFieldMapping?.config?.[index]?.occurrence === 'each_revision'" class="mx-1 text-gray-600">{{ $t('set') }}</span>
                <span class="mx-1 text-gray-600">{{ $t('for') }}</span>
                <SelectElement
                  class="flex-1"
                  name="occurrence"
                  :can-clear="false"
                  rules="required"
                  :native="false"
                  :append-to="appendTo"
                  :items="getOccurrences(customFieldMapping?.config?.[index])"
                  :add-classes="{
                    ElementLayout: {
                      outerWrapper: '!flex',
                      innerWrapperAfter: '!hidden',
                    },
                    SelectElement: {
                      select: {
                        container: '!border-b !border-t-0 !p-0 !min-h-3 h-6 focused:!rounded-none focused:!outline-none  !border-r-0 !border-l-0 !shadow-none !bg-transparent !rounded-none',
                      },
                    },
                  }"
                />
              </div>
              <div class="col-span-12">
                <ListElement
                  v-if="customFieldMapping?.config?.[index]?.occurrence === 'each_revision'"
                  name="revision_config"
                  :disabled="disabled"
                  :add-text="`+ ${$t('Add revision')}`"
                  :presets="['repeatable_list']"
                  rules="required"
                  min="1"
                  :override-classes="{
                    ListElement: {
                      listItem: 'flex w-full bg-gray-50 p-4',
                      remove: ['h-5 w-5 ml-auto mt-2'],
                    },
                  }"
                >
                  <template
                    #default="{ index: revision_index }"
                  >
                    <ObjectElement
                      :embed="true" :name="revision_index" :add-classes="{
                        ElementLayout: {
                          container: 'w-full',
                        },
                      }"
                    >
                      <div class="grid grid-cols-12 w-full items-center">
                        <div class="col-span-6 flex items-center">
                          <div class="text-gray-600 mr-2">
                            {{ $t('Revision') }} {{ revision_index + 1 }}
                          </div>
                          <span class="mx-1 text-gray-600">{{ $t('set') }}</span>
                          <SelectElement
                            class="flex-1"
                            name="source"
                            :native="false"
                            :object="true"
                            :items="getSourceFields(customFieldMapping?.config?.[index])"
                            rules="required"
                            :create="true" :append-new-option="false"
                            :can-clear="false"
                            label-prop="label"
                            value-prop="uid"
                            :groups="true"
                            group-label="label"
                            :append-to="appendTo"
                            :add-classes="{
                              ElementLayout: {
                                outerWrapper: '!flex',
                                innerWrapperAfter: '!hidden',
                              },
                              SelectElement: {
                                select: {
                                  container: '!border-b !border-t-0 !p-0 !min-h-3 h-6 focused:!rounded-none focused:!outline-none  !border-r-0 !border-l-0 !shadow-none !bg-transparent !rounded-none',
                                },
                              },
                            }"
                            @change="revisionSourceChanged(index, revision_index)"
                          />
                        </div>
                        <div class="col-span-4 flex items-center">
                          <span class="mx-1 text-gray-600">{{ $t('to') }}</span>
                          <SelectElement
                            class="flex-1"
                            name="destination"
                            :items="getDestinationFields(customFieldMapping?.config?.[index], customFieldMapping?.config?.[index]?.revision_config?.[revision_index])"
                            label-prop="label"
                            value-prop="uid"
                            :append-to="appendTo"
                            :native="false"
                            :can-clear="false"
                            rules="required"
                            :add-classes="{
                              ElementLayout: {
                                outerWrapper: '!flex',
                                innerWrapperAfter: '!hidden',

                              },
                              SelectElement: {
                                select: {
                                  container: '!border-b !border-t-0 !p-0 !min-h-3 h-6 focused:!rounded-none focused:!outline-none  !border-r-0 !border-l-0 !shadow-none !bg-transparent !rounded-none',
                                },
                              },
                            }"
                          />
                        </div>
                      </div>
                    </ObjectElement>
                  </template>
                </ListElement>
              </div>
            </div>
          </ObjectElement>
        </template>
      </ListElement>
    </div>
  </ObjectElement>
</template>
